import { App, Input, Modal, notification, Select, Typography } from "antd"
import React, { useState, useRef } from "react"
import { Form } from "antd";
import { CloseCircleFilled } from '@ant-design/icons';
import { User } from "../../../data/models/user";
import { makeRequest } from "../../helpers/axios-manipulator";
const { Text } = Typography;

type Props = {
    open: boolean,
    setOpen: Function
    setUserSelected: Function;
    setParentLoading: Function;
}

const NewUserModal = ({ open, setOpen, setUserSelected, setParentLoading }: Props) => {
    const [user, setUser] = useState<User>({ type: 1 } as User)
    const [loading, setLoading] = useState<boolean>(false);
    const [usernameStatus, setUsernameStatus] = useState<"" | "error">("");
    const [emailStatus, setEmailStatus] = useState<"" | "error">("");
    // Remove help message state, use icon instead
    const usernameDebounce = useRef<NodeJS.Timeout | null>(null);
    const emailDebounce = useRef<NodeJS.Timeout | null>(null);
    const { message } = App.useApp();
    const [api, contextHolder] = notification.useNotification();
    const userTypes = [
        { label: "Artist", value: "1" },
        { label: "Collector", value: "2" },
        { label: "3 - Manager", value: "3" },
        { label: "4 - Dealer", value: "4" },
    ]

    const checkUsername = async (username: string) => {
        if (!username) {
            setUsernameStatus("");
            return;
        }
        try {
            const response = await makeRequest({
                origin: window.location.origin,
                method: 'get',
                url: `/api-user/verify-profile/${username}`,
                headers: {},
            });
            if (response && response.exists) {
                setUsernameStatus("error");
            } else {
                setUsernameStatus("");
            }
        } catch (error) {
            setUsernameStatus("error");
        }
    };

    const checkEmail = async (email: string) => {
        if (!email) {
            setEmailStatus("");
            return;
        }
        try {
            const response = await makeRequest({
                origin: window.location.origin,
                method: 'get',
                url: `/api-user/verify-profile/${email}`,
                headers: {},
            });
            if (response && response.exists) {
                setEmailStatus("error");
            } else {
                setEmailStatus("");
            }
        } catch (error) {
            setEmailStatus("error");
        }
    };

    const createUser = async () => {
        if (usernameStatus === "error" || emailStatus === "error") {
            message.error("Please fix errors before creating the user.");
            return;
        }
        const newUser = {
            ...user, isEnabled: "Y", profileName: user.username, publicProfile: [true],
            isArtist: user.type === 1 ? 1 : 0,
            isCollector: user.type === 2 ? 1 : 0,
            artistProfile: {
                username: user.username,
                displayName: user.display_name,
            },
            collectorProfile: {
                username: user.username,
                displayName: user.display_name,
            }
        }
        setLoading(true)
        try {
            // Get JWT from localStorage (adjust if you use a different storage)
            const jwt = localStorage.getItem('jwtToken') || localStorage.getItem('token');
            const response = await makeRequest({
                origin: window.location.origin,
                method: 'post',
                headers: {
                    'Content-Type': 'application/json',
                    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
                },
                url: '/api-users/create',
                data: { user: newUser },
            });
            setUserSelected(response.username)
            setParentLoading(true)
            setParentLoading(false)
            let onboardingFailed = false;
            try {
                await makeRequest({
                    origin: window.location.origin,
                    method: 'post',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    url: '/api-onboarding/create',
                    data: {
                        userid: response.id,
                        username: response.username,
                        notification_status: "OPEN",
                        account_type: response.type,
                        notification_email: response.email,
                        isActive: true,
                    }
                });
            } catch (onboardingError) {
                onboardingFailed = true;
                // Log onboarding error details
                // eslint-disable-next-line no-console
                console.error('[NewUserModal] Onboarding error:', onboardingError);
            }
            setLoading(false)
            setOpen(false)
            setUser({} as User)
            message.success('User created with success!');
            if (onboardingFailed) {
                message.warning('User was created, but onboarding could not be completed.');
            }
        } catch (error: any) {
            setLoading(false)
            // Log error details
            // eslint-disable-next-line no-console
            console.error('[NewUserModal] User creation error:', error);
            let errorMsg = 'Something went wrong during the creation.';
            if (error?.response?.data?.errors) {
                if (Array.isArray(error.response.data.errors)) {
                    errorMsg += ` (${error.response.data.errors.join(', ')})`;
                } else {
                    errorMsg += ` (${error.response.data.errors})`;
                }
            } else if (error?.response?.data?.error) {
                errorMsg += ` (${error.response.data.error})`;
            } else if (error?.message) {
                errorMsg += ` (${error.message})`;
            }
            message.error(errorMsg);
        }
    }

    const enableSubmit = () => {
        if (!user.type || !user.username || !user.display_name || !user.email) return true;
        if (usernameStatus === "error" || emailStatus === "error") return true;
        return false;
    }

    return (
        <Modal
            centered
            closable={false}
            maskClosable={false}
            title="Create new user"
            open={open}
            okText="Create"
            onOk={createUser}
            onCancel={() => { setOpen(false) }}
            width={500}
            okButtonProps={{ disabled: enableSubmit(), loading: loading }}
            destroyOnHidden={true}
        >
            {contextHolder}
            <Text><span style={{ color: "red" }}>*</span> User Type </Text>
            <Select
                placeholder="Select user type"
                style={{ width: '100%' }}
                className="mb-2"
                defaultValue={"1"}
                onChange={(e) => setUser({ ...user, type: parseInt(e) })}
                options={userTypes}
            />

            <Text><span style={{ color: "red" }}>*</span> Username </Text>
            <Form.Item
                validateStatus={usernameStatus}
                style={{ marginBottom: 8 }}
            >
                <Input
                    status={usernameStatus}
                    count={{
                        show: true,
                        max: 20,
                    }}
                    value={user.username || ""}
                    onChange={(e) => {
                        let newValue = (e.target as HTMLInputElement).value.toLowerCase();
                        newValue = newValue.replace(/[^a-z0-9-_]/g, "");
                        if (20 !== undefined) {
                            if (newValue.length > 20) {
                                newValue = newValue.slice(0, 20);
                            }
                        }
                        setUser({ ...user, username: newValue });
                        setUsernameStatus("");
                        if (usernameDebounce.current) clearTimeout(usernameDebounce.current);
                        usernameDebounce.current = setTimeout(() => {
                            checkUsername(newValue);
                        }, 400);
                    }}
                    placeholder="Username"
                    onBlur={() => checkUsername(user.username || "")}
                    suffix={usernameStatus === "error" ? <CloseCircleFilled style={{ color: '#ff4d4f' }} title="Username or email already in use" /> : null}
                />
            </Form.Item>

            <Text><span style={{ color: "red" }}>*</span> Display name </Text>
            <Input
                className="mb-2"
                onChange={(e) => setUser({ ...user, display_name: e.target.value })}
                placeholder="Display name" />
            <Text> <span style={{ color: "red" }}>*</span> Email</Text>
            <Form.Item
                validateStatus={emailStatus}
                style={{ marginBottom: 8 }}
            >
                <Input
                    status={emailStatus}
                    onChange={(e) => {
                        const newValue = (e.target as HTMLInputElement).value.toLowerCase();
                        setUser({ ...user, email: newValue });
                        setEmailStatus("");
                        if (emailDebounce.current) clearTimeout(emailDebounce.current);
                        emailDebounce.current = setTimeout(() => {
                            checkEmail(newValue);
                        }, 400);
                    }}
                    onBlur={() => checkEmail(user.email || "")}
                    placeholder="Email"
                    suffix={emailStatus === "error" ? <CloseCircleFilled style={{ color: '#ff4d4f' }} title="Email or username already in use" /> : null}
                />
            </Form.Item>
        </Modal>
    )
}

export default NewUserModal;